import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import {
    CustomDateRangeDialogComponent,
    CustomDateRangeDialogData,
    CustomDateRangeDialogResult,
} from 'src/app/shared/custom-date-range-dialog/custom-date-range-dialog.component';

export interface TimePeriodOption {
    id: number;
    name: string;
}

export interface DateRange {
    start: Date;
    end: Date;
}

export interface TimePeriodOptionWithRange {
    id: number;
    displayName: string;
}

export class CustomDateRangeController {
    startDateObj?: Date;
    endDateObj?: Date;

    constructor(
        private readonly dialog: MatDialog,
        private readonly dateFormatService: DateFormatService,
        private readonly translate: TranslateService,
    ) {}

    syncFromStrings(start: string | null | undefined, end: string | null | undefined): void {
        this.startDateObj = start ? new Date(start) : undefined;
        this.endDateObj = end ? new Date(end) : undefined;
    }

    openDialog(): Observable<CustomDateRangeDialogResult | undefined> {
        return this.dialog
            .open<CustomDateRangeDialogComponent, CustomDateRangeDialogData, CustomDateRangeDialogResult>(
                CustomDateRangeDialogComponent,
                {
                    data: { startDate: this.startDateObj, endDate: this.endDateObj },
                    panelClass: 'custom-date-range-dialog-panel',
                },
            )
            .afterClosed();
    }

    applyResult(result: CustomDateRangeDialogResult): { startIso: string; endIso: string } {
        this.startDateObj = result.startDate;
        this.endDateObj = result.endDate;
        return {
            startIso: toIsoDate(result.startDate),
            endIso: toIsoDate(result.endDate),
        };
    }

    formatLabel(startStr: string | null | undefined, endStr: string | null | undefined): string {
        const base = this.translate.instant('Custom');
        if (startStr && endStr) {
            const start = this.dateFormatService.formatDateTimeToString(new Date(startStr), this.dateFormatService.monthDayYearFormat);
            const end = this.dateFormatService.formatDateTimeToString(new Date(endStr), this.dateFormatService.monthDayYearFormat);
            return `${base} (${start} – ${end})`;
        }
        return base;
    }

    buildOptionsWithRanges(
        options: TimePeriodOption[],
        customId: number,
        resolver: (id: number, now: Date) => DateRange | null,
    ): TimePeriodOptionWithRange[] {
        const now = new Date();
        return options.map(option => {
            const baseName = this.translate.instant(option.name);
            if (option.id === customId) {
                return { id: option.id, displayName: baseName };
            }
            const range = resolver(option.id, now);
            if (!range) return { id: option.id, displayName: baseName };
            const startLabel = this.dateFormatService.formatDateTimeToString(range.start, this.dateFormatService.monthDayYearFormat);
            const endLabel = this.dateFormatService.formatDateTimeToString(range.end, this.dateFormatService.monthDayYearFormat);
            return { id: option.id, displayName: `${baseName} (${startLabel} – ${endLabel})` };
        });
    }
}

function toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
